(() => {
  if (customElements.get('cl2-guided-highlights-a')) return;
  const EPS = 0.04;
  const LABELS = { idle: 'Start', blocked: 'Start', playing: 'Pause', paused: 'Resume', checkpoint: 'Play next', ended: 'Replay' };

  function validate(times, count, duration) {
    if (times.length !== count - 1) return 'Expected ' + (count - 1) + ' pause times for ' + count + ' highlights, got ' + times.length + '.';
    for (let i = 0; i < times.length; i++) {
      const t = times[i];
      if (!Number.isFinite(t) || t <= 0) return 'Pause ' + (i + 1) + ' must be a number greater than 0.';
      if (i > 0 && t <= times[i - 1]) return 'Pause times must be in ascending order (' + times[i - 1] + 's then ' + t + 's).';
      if (duration && t >= duration - 0.1) return 'Pause ' + (i + 1) + ' (' + t + 's) must be below the video length (' + duration.toFixed(2) + 's).';
    }
    return null;
  }

  class GuidedHighlights extends HTMLElement {
    connectedCallback() {
      // Defer so children are parsed (editor re-renders insert the element before its children).
      if (this.readyState) return;
      this.readyState = 'pending';
      this.initRaf = requestAnimationFrame(() => this.init());
    }
    init() {
      if (!this.isConnected) return;
      this.readyState = 'ready';
      this.video = this.querySelector('video');
      this.btn = this.querySelector('[data-gh-btn]');
      this.items = Array.from(this.querySelectorAll('[data-gh-item]'));
      this.steps = Array.from(this.querySelectorAll('[data-gh-step]'));
      this.live = this.querySelector('[data-gh-live]');
      this.errorEl = this.querySelector('[data-gh-error]');
      this.times = String(this.dataset.pauses || '').split(',').map((s) => s.trim()).filter(Boolean).map(Number);
      this.seg = 0; this.status = 'idle'; this.started = false; this.suspended = false; this.visible = false;
      this.reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.video || !this.btn) return;
      this.video.controls = false; this.video.muted = true; this.video.playsInline = true; this.video.removeAttribute('controls');
      this.video.setAttribute('aria-hidden', 'true'); this.video.setAttribute('disablepictureinpicture', '');
      this.onBtn = () => this.primary();
      this.onEnded = () => { this.seg = this.items.length - 1; this.set('ended'); };
      this.onMeta = () => this.checkSettings();
      this.onTime = () => this.check();
      this.onDoc = () => (document.hidden ? this.suspend() : this.resume());
      this.btn.addEventListener('click', this.onBtn);
      this.video.addEventListener('ended', this.onEnded);
      this.video.addEventListener('loadedmetadata', this.onMeta);
      this.video.addEventListener('timeupdate', this.onTime);
      document.addEventListener('visibilitychange', this.onDoc);
      this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting && e.intersectionRatio >= 0.5; this.visible ? this.resume() : this.suspend(); }, { threshold: [0, 0.5] });
      this.io.observe(this);
      this.checkSettings();
      this.render();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.initRaf);
      if (this.readyState !== 'ready') { this.readyState = null; return; }
      this.readyState = null;
      this.stopWatch();
      this.io && this.io.disconnect();
      document.removeEventListener('visibilitychange', this.onDoc);
      if (this.btn) this.btn.removeEventListener('click', this.onBtn);
      if (this.video) {
        this.video.pause();
        this.video.removeEventListener('ended', this.onEnded);
        this.video.removeEventListener('loadedmetadata', this.onMeta);
        this.video.removeEventListener('timeupdate', this.onTime);
      }
    }
    checkSettings() {
      const d = this.video && Number.isFinite(this.video.duration) ? this.video.duration : undefined;
      this.error = validate(this.times, this.items.length, d);
      if (this.errorEl) { this.errorEl.hidden = !this.error; const m = this.errorEl.querySelector('[data-gh-error-msg]'); if (m) m.textContent = this.error || ''; }
      if (this.error) { this.video.pause(); this.stopWatch(); }
      this.render();
    }
    set(status) { this.status = status; status === 'playing' ? this.startWatch() : this.stopWatch(); this.render(); }
    tryPlay() {
      const p = this.video.play();
      if (p && p.then) p.then(() => this.set('playing')).catch(() => this.set('blocked')); else this.set('playing');
    }
    startWatch() {
      this.stopWatch();
      const v = this.video; const vfc = typeof v.requestVideoFrameCallback === 'function';
      const loop = () => { if (this.status !== 'playing') return; this.check(); this.watchId = vfc ? v.requestVideoFrameCallback(loop) : requestAnimationFrame(loop); };
      this.watchVfc = vfc; loop();
    }
    stopWatch() {
      if (this.watchId == null) return;
      if (this.watchVfc) this.video.cancelVideoFrameCallback && this.video.cancelVideoFrameCallback(this.watchId); else cancelAnimationFrame(this.watchId);
      this.watchId = null;
    }
    check() {
      if (this.status !== 'playing' || this.error) return;
      const t = this.times[this.seg];
      if (t === undefined) return;
      if (this.video.currentTime >= t - EPS) { this.video.pause(); this.video.currentTime = t; this.set('checkpoint'); }
    }
    suspend() { if (this.status === 'playing') { this.suspended = true; this.video.pause(); this.set('paused'); } }
    resume() {
      if (!this.visible || document.hidden || this.error) return;
      if (this.suspended) { this.suspended = false; this.tryPlay(); return; }
      if (!this.started) { this.started = true; this.reducedMq.matches ? this.set('blocked') : this.tryPlay(); }
    }
    primary() {
      if (this.error) return;
      const v = this.video; this.suspended = false; this.started = true;
      if (this.status === 'playing') { v.pause(); this.set('paused'); return; }
      if (this.status === 'ended') { v.currentTime = 0; this.seg = 0; this.render(); this.tryPlay(); return; }
      if (this.status === 'checkpoint') {
        const t = this.times[this.seg];
        if (t !== undefined && Math.abs(v.currentTime - t) > 0.05) v.currentTime = t;
        this.seg = Math.min(this.seg + 1, this.items.length - 1);
        this.render();
      }
      this.tryPlay();
    }
    render() {
      const n = this.items.length, s = this.seg, st = this.status;
      this.items.forEach((el, i) => { const on = i === s; if (on && el.hidden) { el.classList.remove('is-entering'); void el.offsetWidth; el.classList.add('is-entering'); } el.hidden = !on; });
      this.steps.forEach((el, i) => { el.classList.toggle('is-done', i < s); el.classList.toggle('is-current', i === s); if (i === s) el.setAttribute('aria-current', 'step'); else el.removeAttribute('aria-current'); });
      const label = this.error ? 'Unavailable' : LABELS[st];
      this.btn.disabled = !!this.error;
      this.btn.classList.toggle('is-ghost', st === 'playing');
      this.btn.querySelector('[data-gh-label]').textContent = label;
      this.btn.setAttribute('aria-label', label + (st === 'checkpoint' ? ': highlight ' + (s + 2) + ' of ' + n : ''));
      this.querySelectorAll('[data-gh-icon]').forEach((ic) => { ic.hidden = ic.dataset.ghIcon !== (st === 'playing' ? 'pause' : st === 'ended' ? 'replay' : 'play'); });
      const item = this.items[s];
      if (this.live && item) {
        const msg = 'Highlight ' + (s + 1) + ' of ' + n + ': ' + item.textContent.replace(/\s+/g, ' ').trim() + (st === 'ended' ? '. Finished, replay available.' : st === 'checkpoint' ? '. Paused, press Play next to continue.' : '');
        if (this.live.textContent !== msg) this.live.textContent = msg;
      }
    }
  }
  customElements.define('cl2-guided-highlights-a', GuidedHighlights);
})();
